/**
 * OrgTreeFilter — boczny panel filtra „struktura organizacyjna" dla list dokumentów
 * zawężanych po pracowniku (moduł Urlopy: wnioski urlopowe na liście dokumentów specjalnych).
 *
 * Panel pokazuje drzewo jednostek firmy z checkboxami. Zaznaczenie działu zaznacza WSZYSTKICH
 * jego pracowników i podjednostki (trójstan: pełny / częściowy / pusty), zaznaczenie osoby
 * zawęża do niej. Wynik to lista id pracowników — strona-host wpina ją w filtr nagłówka
 * (`field: 'employee', mode: 'values'`), dzięki czemu filtruje się ZARÓWNO lista, jak i
 * kalendarz (oba jadą z tego samego zapytania listy).
 *
 * Degradacja (ważna — panel nie może blokować pracy):
 * - brak wybranej firmy („Wszystkie firmy") — nie ma jednego drzewa, więc płaska lista osób,
 * - drzewo niedostępne (np. 403 dla usera bez prawa do zarządzania strukturą) — j/w, płaska
 *   lista + dyskretna notka; NIGDY pusty panel ani błąd blokujący.
 *
 * Wyjątek: nieudane pobranie samej listy pracowników nie ma czym się zdegradować (bez osób panel
 * jest bezużyteczny), więc pokazuje czerwony komunikat błędu z przyciskiem „Spróbuj ponownie" —
 * spójnie z listami i ustawieniami Kadr. Ponowienie odświeża zapytania, które padły (pracownicy
 * i, jeśli też padła przejściowo, struktura — wtedy po sukcesie wraca drzewo zamiast płaskiej
 * listy; błąd 403 struktury to stała degradacja, więc go nie ponawiamy). Komunikat zostaje na
 * ekranie na czas ponowienia (przycisk pokazuje ładowanie), a nie znika na rzecz szkieletu — także
 * wtedy, gdy równolegle wraca do ładowania struktura (błąd pracowników ma pierwszeństwo przed jej
 * szkieletem).
 *
 * To NIE jest popover — panel renderuje się w układzie strony (kolumna obok listy), więc
 * świadomie nie portalujemy (zasada portalowania z CLAUDE.md §4.12 dotyczy nakładek).
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import {
  classifyListLoadError,
  isRetryableListLoadError,
} from '@/components/configurable-table/load-error';
import { IconButton } from '@/components/IconButton';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { useEmployeesList, useOrgUnitsTree } from '@/lib/use-tenant-people';
import { cn } from '@/lib/utils';

import {
  buildEmployeeNodes,
  buildOrgTree,
  collectUnitIds,
  deriveUnitCheckState,
  filterEmployeeNodes,
  filterOrgTree,
  type OrgTreeEmployeeNode,
  type OrgTreeUnitNode,
  toggleEmployeeSelection,
  toggleUnitSelection,
} from './build-tree';
import { TriStateCheckbox } from './TriStateCheckbox';

/**
 * Silnik list przycina `?limit=` do 200 (`apps/api/lib/list-engine/typed-executor.ts`) — bierzemy
 * maksimum, a gdy firma ma więcej osób, mówimy o tym wprost zamiast po cichu gubić ludzi.
 */
const EMPLOYEE_LIMIT = 200;

export interface OrgTreeFilterProps {
  /** Aktywna firma; `null` w trybie „Wszystkie firmy" — wtedy nie ma jednego drzewa. */
  companyId: string | null;
  selectedEmployeeIds: string[];
  onChange: (ids: string[]) => void;
  className?: string;
}

interface UnitBranchProps {
  node: OrgTreeUnitNode;
  depth: number;
  selectedIds: ReadonlySet<string>;
  expanded: (unitId: string, depth: number) => boolean;
  onToggleExpand: (unitId: string, next: boolean) => void;
  onToggleUnit: (node: OrgTreeUnitNode, next: boolean) => void;
  onToggleEmployee: (employeeId: string, next: boolean) => void;
  expandLabel: string;
  collapseLabel: string;
}

function EmployeeLeaf({
  employee,
  depth,
  checked,
  onToggle,
}: {
  employee: OrgTreeEmployeeNode;
  depth: number;
  checked: boolean;
  onToggle: (employeeId: string, next: boolean) => void;
}) {
  return (
    <li
      role="treeitem"
      aria-checked={checked}
      className="flex items-center gap-1 py-0.5"
      style={{ paddingLeft: `${depth * 0.75 + 1.75}rem` }}
      data-testid={`org-tree-filter-employee-${employee.id}`}
    >
      <Checkbox
        checked={checked}
        onChange={(e) => onToggle(employee.id, e.target.checked)}
        // Enter na checkboxie natywnie NIC nie robi (to submit formularza) — dokładamy go
        // ręcznie; Spacja działa natywnie, więc jej NIE obsługujemy (podwójny toggle).
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onToggle(employee.id, !checked);
          }
        }}
        label={employee.name}
        containerClassName="min-w-0"
        data-testid={`org-tree-filter-employee-${employee.id}-checkbox`}
      />
    </li>
  );
}

function UnitBranch({
  node,
  depth,
  selectedIds,
  expanded,
  onToggleExpand,
  onToggleUnit,
  onToggleEmployee,
  expandLabel,
  collapseLabel,
}: UnitBranchProps) {
  const state = deriveUnitCheckState(node, selectedIds);
  const hasChildren = node.children.length > 0 || node.employees.length > 0;
  const isOpen = hasChildren && expanded(node.id, depth);

  return (
    <li
      role="treeitem"
      aria-expanded={hasChildren ? isOpen : undefined}
      aria-checked={state === 'indeterminate' ? 'mixed' : state === 'checked'}
      data-testid={`org-tree-filter-unit-${node.id}`}
    >
      <div className="flex items-center gap-1 py-0.5" style={{ paddingLeft: `${depth * 0.75}rem` }}>
        {hasChildren ? (
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={isOpen ? collapseLabel : expandLabel}
            aria-expanded={isOpen}
            onClick={() => onToggleExpand(node.id, !isOpen)}
            data-testid={`org-tree-filter-unit-${node.id}-toggle`}
          >
            {isOpen ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
          </IconButton>
        ) : (
          <span className="h-7 w-7 shrink-0" aria-hidden="true" />
        )}
        <TriStateCheckbox
          state={state}
          onChange={(e) => onToggleUnit(node, e.target.checked)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onToggleUnit(node, state !== 'checked');
            }
          }}
          label={<span className="font-medium">{node.name}</span>}
          containerClassName="min-w-0"
          data-testid={`org-tree-filter-unit-${node.id}-checkbox`}
        />
      </div>

      {isOpen && (
        <ul role="group">
          {node.children.map((child) => (
            <UnitBranch
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedIds={selectedIds}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              onToggleUnit={onToggleUnit}
              onToggleEmployee={onToggleEmployee}
              expandLabel={expandLabel}
              collapseLabel={collapseLabel}
            />
          ))}
          {node.employees.map((employee) => (
            <EmployeeLeaf
              key={employee.id}
              employee={employee}
              depth={depth + 1}
              checked={selectedIds.has(employee.id)}
              onToggle={onToggleEmployee}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function OrgTreeFilter({
  companyId,
  selectedEmployeeIds,
  onChange,
  className,
}: OrgTreeFilterProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  // Nadpisania rozwinięcia per węzeł; brak wpisu = domyślne (korzenie rozwinięte, reszta nie).
  const [expandOverrides, setExpandOverrides] = useState<Record<string, boolean>>({});

  const treeQ = useOrgUnitsTree(companyId);
  const employeesQ = useEmployeesList({
    companyId: companyId ?? undefined,
    isActive: true,
    limit: EMPLOYEE_LIMIT,
  });

  const employees = useMemo(() => employeesQ.data?.employees ?? [], [employeesQ.data?.employees]);
  const selectedSet = useMemo(() => new Set(selectedEmployeeIds), [selectedEmployeeIds]);

  // Bez wybranej firmy nie ma jednego drzewa; 403/500 na drzewie też nie może blokować filtra.
  const treeUnavailable = companyId === null || treeQ.isError;

  const tree = useMemo(
    () =>
      buildOrgTree({
        units: treeQ.data?.tree ?? [],
        employees,
        unassignedLabel: t('tenant.orgTreeFilter.unassignedGroup'),
      }),
    [treeQ.data?.tree, employees, t],
  );
  const flatEmployees = useMemo(() => buildEmployeeNodes(employees), [employees]);

  const visibleTree = useMemo(() => filterOrgTree(tree, query), [tree, query]);
  const visibleFlat = useMemo(
    () => filterEmployeeNodes(flatEmployees, query),
    [flatEmployees, query],
  );

  // Przy aktywnym wyszukiwaniu rozwijamy wszystko, co przetrwało filtr — inaczej trafienie
  // schowane w zwiniętej podjednostce byłoby niewidoczne.
  const searching = query.trim() !== '';
  const forcedOpenIds = useMemo(
    () => (searching ? new Set(collectUnitIds(visibleTree)) : null),
    [searching, visibleTree],
  );

  const expanded = useCallback(
    (unitId: string, depth: number) => {
      if (forcedOpenIds) return forcedOpenIds.has(unitId);
      return expandOverrides[unitId] ?? depth === 0;
    },
    [forcedOpenIds, expandOverrides],
  );

  const handleToggleExpand = useCallback((unitId: string, next: boolean) => {
    setExpandOverrides((prev) => ({ ...prev, [unitId]: next }));
  }, []);

  const handleToggleUnit = useCallback(
    (node: OrgTreeUnitNode, next: boolean) => {
      onChange(toggleUnitSelection(node, selectedEmployeeIds, next));
    },
    [onChange, selectedEmployeeIds],
  );

  const handleToggleEmployee = useCallback(
    (employeeId: string, next: boolean) => {
      onChange(toggleEmployeeSelection(employeeId, selectedEmployeeIds, next));
    },
    [onChange, selectedEmployeeIds],
  );

  // Ponowienie po błędzie dotyczy KONKRETNEJ firmy: `undefined` = nic nie ponawiamy, inaczej id firmy
  // (`null` = „Wszystkie firmy"). Strona montuje panel bez `key`, więc zmiana firmy w trakcie
  // ponowienia zostawia ten stan — a bez porównania z bieżącą firmą nowa firma (jej pracownicy
  // dopiero się ładują) pokazałaby czerwony błąd cudzego ponowienia.
  const [retryingFor, setRetryingFor] = useState<string | null | undefined>(undefined);
  const retryingThisCompany = retryingFor === companyId;
  // Komunikat błędu zostaje zamontowany na czas ponowienia po błędzie: po starcie pobrania zapytanie
  // bez danych traci błąd i wraca do `pending`, więc bez tego alert zniknąłby (a z nim przycisk
  // z fokusem) na rzecz szkieletu. Flaga wstaje przy kliknięciu i opada, gdy pobranie się rozstrzygnie
  // — sukces pokazuje listę, ponowna porażka zostawia alert.
  const retainErrorAlert = retryingThisCompany && employeesQ.isPending;
  const showEmployeesError = employeesQ.isError || retainErrorAlert;

  // Szkielet: pracownicy — `isPending` (brak danych), NIE `isLoading` (= pending ORAZ pobieranie).
  // Zapytanie bez danych, którego pobranie jest wstrzymane (brak sieci — `fetchStatus: 'paused'`),
  // ma `isLoading === false` i nie ma błędu — z `isLoading` wpadłoby w fałszywy pusty stan „Brak
  // pracowników do wyboru". To samo dotyczy struktury: bez niej `buildOrgTree` wrzuciłby wszystkich
  // do „Bez działu" (fałszywa struktura). `treeQ.isPending` czytamy tylko za `!treeUnavailable`
  // (firma wybrana); zapytanie struktury przy wybranej firmie nigdy nie jest wyłączone, więc
  // `isPending` nie zawiśnie przez `enabled: false`. Bez sieci świadomie trzymamy szkielet do
  // powrotu połączenia — zamiast fałszywej grupy „Bez działu".
  // Błąd pracowników ma pierwszeństwo przed szkieletem struktury: gdy padły oba zapytania i po
  // ponowieniu struktura wraca do ładowania, a pracownicy zdążą paść ponownie, komunikat nie może
  // zniknąć na rzecz szkieletu do czasu, aż struktura się rozstrzygnie.
  const showSkeleton =
    (employeesQ.isPending && !retainErrorAlert) ||
    (!showEmployeesError && !treeUnavailable && treeQ.isPending);

  // Ponowienie po błędzie: odświeżamy zapytania, które padły. Błąd struktury sam z siebie NIE
  // zapala komunikatu (to degradacja do płaskiej listy), ale gdy użytkownik i tak ponawia pobranie
  // osób, dociągamy też strukturę — inaczej po sukcesie zostałaby płaska lista mimo przejściowej
  // awarii drzewa. Struktury NIE ponawiamy przy błędzie, który się nie zmieni (np. 403 — brak prawa
  // do zarządzania strukturą — to udokumentowana degradacja do płaskiej listy, nie awaria).
  // Zapytanie struktury bez firmy jest wyłączone (`enabled: false`) — nigdy nie jest w błędzie
  // ani w pobieraniu, więc go tu nie ruszamy.
  const retryFailed = () => {
    const retryCompanyId = companyId;
    setRetryingFor(retryCompanyId);
    // Flagę zdejmujemy tylko, jeśli nadal dotyczy tego ponowienia (użytkownik mógł w międzyczasie
    // zmienić firmę i kliknąć ponowienie dla innej).
    void employeesQ
      .refetch()
      .finally(() => setRetryingFor((prev) => (prev === retryCompanyId ? undefined : prev)));
    if (treeQ.isError && isRetryableListLoadError(classifyListLoadError(treeQ.error))) {
      void treeQ.refetch();
    }
  };

  const total = employeesQ.data?.total ?? employees.length;
  const truncated = total > employees.length;

  const expandLabel = t('tenant.orgTreeFilter.expandNode');
  const collapseLabel = t('tenant.orgTreeFilter.collapseNode');

  return (
    <aside
      className={cn(
        'flex min-h-0 flex-col gap-2 rounded-lg border border-border-primary bg-bg-primary p-3',
        className,
      )}
      aria-label={t('tenant.orgTreeFilter.title')}
      data-testid="org-tree-filter"
    >
      <div className="flex shrink-0 items-center justify-between gap-2">
        <h2 className="truncate text-sm font-semibold text-text-primary">
          {t('tenant.orgTreeFilter.title')}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange([])}
          disabled={selectedEmployeeIds.length === 0}
          data-testid="org-tree-filter-clear"
        >
          {t('tenant.orgTreeFilter.clear')}
        </Button>
      </div>

      <p className="shrink-0 text-xs text-text-tertiary" data-testid="org-tree-filter-count">
        {t('tenant.orgTreeFilter.selectedCount', { value: selectedEmployeeIds.length })}
      </p>

      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder={t('tenant.orgTreeFilter.searchPlaceholder')}
        className="shrink-0"
        data-testid="org-tree-filter-search"
      />

      {/* Notka o płaskiej liście nie towarzyszy błędowi pobrania pracowników (listy i tak nie ma). */}
      {treeUnavailable && !showSkeleton && !showEmployeesError && (
        <p className="shrink-0 text-xs text-text-muted" data-testid="org-tree-filter-flat-note">
          {t('tenant.orgTreeFilter.noStructureFallback')}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {showSkeleton ? (
          <div className="flex flex-col gap-2 py-1" data-testid="org-tree-filter-loading">
            <Skeleton height="1.25rem" />
            <Skeleton height="1.25rem" />
            <Skeleton height="1.25rem" />
            <Skeleton height="1.25rem" />
          </div>
        ) : showEmployeesError ? (
          <Alert
            tone="error"
            className="my-1"
            actions={
              // Spinner przycisku = ponowienie pobrania pracowników (to na nie czeka użytkownik);
              // obce pobranie struktury w tle go nie zapala.
              <Button
                variant="secondary"
                size="sm"
                onClick={retryFailed}
                loading={retryingThisCompany}
                data-testid="org-tree-filter-error-retry"
              >
                {t('common.retry')}
              </Button>
            }
            data-testid="org-tree-filter-error"
          >
            {t('tenant.orgTreeFilter.loadError')}
          </Alert>
        ) : treeUnavailable ? (
          visibleFlat.length === 0 ? (
            <p className="py-2 text-xs text-text-muted" data-testid="org-tree-filter-empty">
              {t('tenant.orgTreeFilter.empty')}
            </p>
          ) : (
            <ul role="tree" aria-label={t('tenant.orgTreeFilter.title')}>
              {visibleFlat.map((employee) => (
                <EmployeeLeaf
                  key={employee.id}
                  employee={employee}
                  depth={0}
                  checked={selectedSet.has(employee.id)}
                  onToggle={handleToggleEmployee}
                />
              ))}
            </ul>
          )
        ) : visibleTree.length === 0 ? (
          <p className="py-2 text-xs text-text-muted" data-testid="org-tree-filter-empty">
            {t('tenant.orgTreeFilter.empty')}
          </p>
        ) : (
          <ul role="tree" aria-label={t('tenant.orgTreeFilter.title')}>
            {visibleTree.map((node) => (
              <UnitBranch
                key={node.id}
                node={node}
                depth={0}
                selectedIds={selectedSet}
                expanded={expanded}
                onToggleExpand={handleToggleExpand}
                onToggleUnit={handleToggleUnit}
                onToggleEmployee={handleToggleEmployee}
                expandLabel={expandLabel}
                collapseLabel={collapseLabel}
              />
            ))}
          </ul>
        )}
      </div>

      {truncated && (
        <p
          className="shrink-0 text-xs text-[rgb(var(--color-warning-text))]"
          data-testid="org-tree-filter-truncated"
        >
          {t('tenant.orgTreeFilter.truncatedNotice', { shown: employees.length, total })}
        </p>
      )}
    </aside>
  );
}
